import { Decimal } from 'decimal.js';
import { lineAmounts, sumTotals } from './money.js';
import type { MaintenanceCategory } from './maintenance.js';

/** Prestation du catalogue telle qu'elle sert au calcul (montants en chaînes décimales). */
export interface CatalogItemForLines {
  label: string;
  maintenanceCategory: MaintenanceCategory | null;
  laborHours: string;
  vatRate: string;
  parts: readonly { label: string; reference: string | null; quantity: string; unitPriceHt: string }[];
}

export interface CatalogLine {
  kind: 'part' | 'labor';
  maintenanceCategory: MaintenanceCategory | null;
  label: string;
  reference: string | null;
  quantity: string;
  unitPriceHt: string | null;
  vatRate: string;
}

export const LABOR_LINE_LABEL = 'Main-d’œuvre';

const isZero = (v: string) => new Decimal(v).isZero();

/**
 * Main-d'œuvre HT d'une prestation : temps (heures) × taux horaire HT, arrondi au centime (même calcul
 * qu'une ligne : quantité = heures, prix unitaire = taux). `null` sans taux horaire.
 */
export function laborAmountHt(laborHours: string, hourlyRateHt: string | null): string | null {
  if (isZero(laborHours)) return '0.00';
  if (hourlyRateHt === null) return null;
  return lineAmounts({ quantity: laborHours, unitPriceHt: hourlyRateHt, vatRate: '0' }).totalHt;
}

/**
 * Lignes de travaux complémentaires préremplies depuis une prestation : une ligne de main-d'œuvre
 * (quantité = heures, prix = taux horaire HT ; omise pour 0 h), puis les pièces types.
 */
export function catalogLines(item: CatalogItemForLines, hourlyRateHt: string | null): CatalogLine[] {
  const lines: CatalogLine[] = [];
  if (!isZero(item.laborHours)) {
    lines.push({
      kind: 'labor',
      maintenanceCategory: item.maintenanceCategory,
      label: LABOR_LINE_LABEL,
      reference: null,
      quantity: item.laborHours,
      unitPriceHt: hourlyRateHt,
      vatRate: item.vatRate,
    });
  }
  for (const p of item.parts) {
    lines.push({
      kind: 'part',
      maintenanceCategory: item.maintenanceCategory,
      label: p.label,
      reference: p.reference,
      quantity: p.quantity,
      unitPriceHt: p.unitPriceHt,
      vatRate: item.vatRate,
    });
  }
  return lines;
}

/** Totaux estimés d'une prestation ; `null` tant qu'une ligne n'a pas de prix (taux horaire absent). */
export function catalogTotals(item: CatalogItemForLines, hourlyRateHt: string | null) {
  const lines = catalogLines(item, hourlyRateHt);
  if (lines.some((l) => l.unitPriceHt === null)) return null;
  return sumTotals(lines.map((l) => ({ quantity: l.quantity, unitPriceHt: l.unitPriceHt as string, vatRate: l.vatRate })));
}

/** Petit atelier : l'envoi par le mécanicien sans validation est proposé par défaut jusqu'à ce seuil de membres. */
export const SMALL_WORKSHOP_MAX_MEMBERS = 3;
